import type { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiPharmacyContract } from '@montugroup/circuit-api-contracts';
import { formatEscriptToken } from '@montugroup/circuit-shared';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import getAuthBearerToken from '@montugroup/circuit-shared/utils/getAuthBearerToken';
import { useQueryClient } from '@tanstack/react-query';
import type { ClientInferResponseBody } from '@ts-rest/core';

export type SubmitPrescriptionResponse = ClientInferResponseBody<
  typeof apiPharmacyContract.submitPrescription,
  typeof HttpStatus.OK
>['data'];

const client = getTsPharmacyClient();

/**
 * Handles the token submission for pharmacists on behalf of users
 * @param token - the eScript token string
 * @param onSuccessCallback - optional on success function for valid tokens
 * @param onErrorCallback - optional on error function for invalid tokens
 * @returns lifecycle methods from react query and onboarding data
 */
export function useSubmitPrescription() {
  const queryClient = useQueryClient();
  const { mutate, data, error, isPending, isSuccess } = client.submitPrescription.useMutation();

  const submitPrescriptionMutate = (
    token: string,
    onSuccessCallback?: (data: SubmitPrescriptionResponse) => void,
    onErrorCallback?: (error?: unknown) => void,
  ) => {
    mutate(
      {
        headers: { authorization: getAuthBearerToken() },
        body: {
          data: {
            token: formatEscriptToken(token),
          },
        },
      },
      {
        onSuccess: (response) => {
          queryClient.invalidateQueries({
            queryKey: [...client.listPharmacyReferrals.keys],
          });

          onSuccessCallback?.(response.body.data);
        },
        onError: (error) => {
          onErrorCallback?.(error);
        },
      },
    );
  };

  return {
    submitPrescriptionMutate,
    submitPrescriptionResponse: data?.body.data,
    error,
    isPending,
    isSuccess,
  };
}
